import { Stack } from '@chakra-ui/react';
import React from 'react';

import { TextField } from 'Containers/App/types';
import { getCoverageDisclaimerKey } from 'Containers/App/utils';
import {
  ConditionsContainer,
  DetailsWrapper,
} from 'Containers/ViewBenefitPage/ExpandablePlanCard/PlanContent/styled';
import { LtcBenefits } from 'Containers/ViewBenefitPage/types';
import { transformBenefitItems } from 'Containers/ViewBenefitPage/utils';
import { RichTextContent } from 'ContentfulDefaults/types/_shared';
import { ContentfulSupplementalPlan } from 'ContentfulDefaults/types/benefitsAdditional';
import { useTextContext } from 'Contexts/textContext';
import { H5, Paragraph } from 'DesignLibrary/atoms';
import RichText from 'Shared/RichText';
import Text from 'Shared/Text';
import { SupplementalPlan } from 'Types/entities';
import { toDollars } from 'Utils/helpers';

export interface LtcPlanContentProps {
  plan: SupplementalPlan;
  contentfulSupplementalPlan?: ContentfulSupplementalPlan | undefined;
}

export interface TextProps {
  headerField: TextField;
  textField?: TextField;
  textField1?: TextField;
  textField2?: TextField;
  richText?: RichTextContent;
  dataTestId?: string;
  textFieldAmount?: TextField;
  payment_type?: string;
  section?: string;
}

export const LtcPlanContent = ({ plan, contentfulSupplementalPlan }: LtcPlanContentProps) => {
  const { retrieveContentfulData } = useTextContext();
  const benefits = transformBenefitItems(plan) as LtcBenefits;

  const prefix = 'benefits_section_ltc.who_you_can_cover';

  const availableToSpouse = benefits?.covers_spouse_partner;
  const availableToPartner = benefits?.has_partner_benefits_life_ltc;
  const availableToChildren = benefits?.covers_dependents;

  const result = getCoverageDisclaimerKey({
    availableToSpouse,
    availableToPartner,
    availableToChildren,
    prefix,
  });

  const eliminationPeriod = benefits?.elimination_period;

  const paymentPercentage = benefits?.payment_percentage;

  const paymentNumMonths = benefits?.payment_num_months;

  const paymentAmount = benefits?.payment_amount;

  const hasOutroNote = benefits?.has_outro_note;

  const displayName = retrieveContentfulData<string>(
    'benefits_section_additional.life_ltc_benefit.card_title',
  );

  const renderEvidenceTip = () => ({
    text: (
      <p>
        <Text field="benefits_section_ltc.tooltips.yourAge" />
      </p>
    ),
  });

  const containerStack = ({ headerField, textField, dataTestId, section }: TextProps) => (
    <Stack gap={2} data-testid={dataTestId}>
      <Stack>
        <H5>
          <Text field={headerField} />
        </H5>
        <Paragraph as="div" lineHeight={1.75} size="small" className="plan-description">
          {dataTestId !== 'evidence_of_insurability' && (
            <Text
              field={textField}
              vars={section === 'who_you_can_cover' ? { plan: displayName } : undefined}
            />
          )}
          {dataTestId === 'evidence_of_insurability' && (
            <RichText inlineParagraphs toolTips={[renderEvidenceTip()]} field={textField} />
          )}
        </Paragraph>
      </Stack>
    </Stack>
  );

  const containerStackLongTermCareCoverage = ({
    headerField,
    textField1,
    textField2,
    dataTestId,
    textFieldAmount,
    payment_type,
  }: TextProps) => (
    <Stack gap={2} data-testid={dataTestId}>
      <Stack>
        <H5>
          <Text field={headerField} />
        </H5>
        <Paragraph as="div" lineHeight={1.75} size="small" className="plan-description">
          <Text field={textField1} vars={{ eliminationPeriod }} />
        </Paragraph>
        {payment_type === 'percentage' && (
          <Paragraph as="div" lineHeight={1.75} size="small" className="plan-description">
            <Text field={textField2} vars={{ paymentPercentage, paymentNumMonths }} />
          </Paragraph>
        )}
        {payment_type === 'flat_amount' && (
          <Paragraph as="div" lineHeight={1.75} size="small" className="plan-description">
            <Text field={textFieldAmount} vars={{ paymentAmount: toDollars(paymentAmount) }} />
          </Paragraph>
        )}
      </Stack>
    </Stack>
  );

  const outroTextNoteShort: RichTextContent | undefined =
    contentfulSupplementalPlan?.outro_note_short || undefined;
  const outroTextNoteLong: RichTextContent | undefined =
    contentfulSupplementalPlan?.outro_note_text || undefined;

  const textNoteStack = (
    <Stack data-testid="more-about-section">
      <H5>
        <Text field="benefits_section_ltc.header_more_about_this_plan" />
      </H5>
      <Stack gap={3}>
        <ConditionsContainer>
          <Paragraph size="small">
            <RichText
              field={undefined}
              useStylingWrapper
              richTextStyling={{ p: { size: 'small' } }}
              fieldContent={outroTextNoteShort}
              inlineParagraphs={false}
              maxHeaderSize={5}
            />
          </Paragraph>
          {outroTextNoteLong && (
            <Paragraph size="small">
              <RichText
                field={undefined}
                useStylingWrapper
                richTextStyling={{ p: { size: 'small' } }}
                fieldContent={outroTextNoteLong}
                inlineParagraphs={false}
                maxHeaderSize={5}
              />
            </Paragraph>
          )}
        </ConditionsContainer>
      </Stack>
    </Stack>
  );

  return (
    <DetailsWrapper data-testid="ltc_section">
      {/* Who you can cover */}
      {containerStack({
        headerField: 'benefits_section_ltc.header_who_you_can_cover',
        textField: result as TextField,
        dataTestId: result,
        section: 'who_you_can_cover',
      })}

      {/* Portability */}
      {benefits?.portability &&
        containerStack({
          headerField: 'benefits_section_ltc.header_portability',
          textField: 'benefits_section_ltc.portability_text',
          dataTestId: 'portability',
        })}

      {/* Evidence of insurability */}
      {benefits?.has_eoi &&
        containerStack({
          headerField: 'benefits_section_ltc.header_evidence_of_insurability',
          textField: 'benefits_section_ltc.evidence_of_insurability',
          dataTestId: 'evidence_of_insurability',
        })}

      {/* Long-term care coverage */}
      {containerStackLongTermCareCoverage({
        headerField: 'benefits_section_ltc.header_long_term_care_coverage',
        payment_type: benefits?.payment_type,
        textField1: 'benefits_section_ltc.long_term_care_coverage_para1_text',
        textField2: 'benefits_section_ltc.long_term_care_coverage_para2_text',
        textFieldAmount: 'benefits_section_ltc.long_term_care_coverage_para2_amount_text',
        dataTestId: benefits?.payment_type,
      })}

      {/* More About this plan section */}
      {hasOutroNote && textNoteStack}
    </DetailsWrapper>
  );
};
